import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { BranchHealthRow } from '../../types';
import { QualityIndicator } from '../common/QualityIndicator';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import type { QualityStatus } from '../../types';
import { useAuth } from '../auth/TeamsAuthProvider';

const useStyles = makeStyles({
  card: {
    flex: '1 1 100%',
    minWidth: '600px',
  },
  content: {
    padding: tokens.spacingHorizontalM,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerCell: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'left' as const,
    cursor: 'pointer',
    userSelect: 'none',
  },
  rowCell: {
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: 'left' as const,
  },
  branchName: {
    fontWeight: '600',
  },
  metricValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  directionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

interface BranchHealthGridProps {
  data: BranchHealthRow[] | null;
  isLoading?: boolean;
}

type SortDirection = 'asc' | 'desc' | null;
type SortField = 'branch_name' | 'inbound' | 'outbound' | 'calls' | null;

function healthStatusToBadge(status: 'good' | 'warning' | 'poor') {
  switch (status) {
    case 'good':
      return { label: 'Good', color: 'success' };
    case 'warning':
      return { label: 'Warning', color: 'warning' };
    case 'poor':
      return { label: 'Poor', color: 'error' };
  }
}

function getHealthStatusIndicator(status: 'good' | 'warning' | 'poor'): QualityStatus {
  return status as QualityStatus;
}

export function BranchHealthGrid({ data, isLoading }: BranchHealthGridProps) {
  const styles = useStyles();
  const { user } = useAuth();
  const locationSingular = user?.location_label_singular ?? 'Branch';
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  if (isLoading || !data) {
    return (
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">{locationSingular} Quality Health</Text>} />
        <div className={styles.content}>
          <LoadingState variant="panel" layout="table" />
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={styles.card}>
        <CardHeader header={<Text weight="semibold">{locationSingular} Quality Health</Text>} />
        <EmptyState
          compact
          title={`No ${locationSingular.toLowerCase()} data available for the selected period`}
        />
      </Card>
    );
  }

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedData = [...data];
  if (sortField && sortDirection) {
    sortedData.sort((a, b) => {
      let compareA: string | number;
      let compareB: string | number;

      switch (sortField) {
        case 'branch_name':
          compareA = a.branch_name;
          compareB = b.branch_name;
          break;
        case 'inbound':
          compareA = a.inbound_avg_jitter_ms + a.inbound_avg_latency_ms + a.inbound_avg_packet_loss_percent;
          compareB = b.inbound_avg_jitter_ms + b.inbound_avg_latency_ms + b.inbound_avg_packet_loss_percent;
          break;
        case 'outbound':
          compareA = a.outbound_avg_jitter_ms + a.outbound_avg_latency_ms + a.outbound_avg_packet_loss_percent;
          compareB = b.outbound_avg_jitter_ms + b.outbound_avg_latency_ms + b.outbound_avg_packet_loss_percent;
          break;
        case 'calls':
          compareA = a.call_count;
          compareB = b.call_count;
          break;
        default:
          return 0;
      }

      if (typeof compareA === 'string') {
        // Both compareA and compareB always come from the same field per
        // switch case above, so compareB is a string here too even though
        // its declared type is the shared `string | number` union.
        return sortDirection === 'asc'
          ? compareA.localeCompare(compareB as string)
          : (compareB as string).localeCompare(compareA);
      }

      return sortDirection === 'asc'
        ? (compareA || 0) - ((compareB as number) || 0)
        : ((compareB as number) || 0) - (compareA || 0);
    });
  }

  return (
    <Card className={styles.card}>
      <CardHeader header={<Text weight="semibold">{locationSingular} Quality Health</Text>} />
      <div className={styles.content}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.headerCell} onClick={() => handleSort('branch_name')}>
                <Text weight="semibold">
                  {locationSingular} Name{getSortIndicator('branch_name')}
                </Text>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('inbound')}>
                <Text weight="semibold">
                  Inbound Health{getSortIndicator('inbound')}
                </Text>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('outbound')}>
                <Text weight="semibold">
                  Outbound Health{getSortIndicator('outbound')}
                </Text>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('calls')}>
                <Text weight="semibold">
                  Call Count{getSortIndicator('calls')}
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((branch) => {
              const inboundBadge = healthStatusToBadge(branch.inbound_health_status);
              const outboundBadge = healthStatusToBadge(branch.outbound_health_status);

              return (
                <tr key={branch.branch_name}>
                  <td className={styles.rowCell}>
                    <Text className={styles.branchName}>{branch.branch_name}</Text>
                  </td>
                  <td className={styles.rowCell}>
                    <div className={styles.directionBadge}>
                      <QualityIndicator
                        status={getHealthStatusIndicator(branch.inbound_health_status)}
                        size="small"
                      />
                      <Text className={styles.metricValue}>
                        {inboundBadge.label}
                        <br />
                        <span style={{ fontSize: '0.85em', color: tokens.colorNeutralForeground4 } as CSSProperties}>
                          J: {branch.inbound_avg_jitter_ms.toFixed(1)}ms | L:{' '}
                          {branch.inbound_avg_latency_ms.toFixed(1)}ms | PL:{' '}
                          {branch.inbound_avg_packet_loss_percent.toFixed(2)}%
                        </span>
                      </Text>
                    </div>
                  </td>
                  <td className={styles.rowCell}>
                    <div className={styles.directionBadge}>
                      <QualityIndicator
                        status={getHealthStatusIndicator(branch.outbound_health_status)}
                        size="small"
                      />
                      <Text className={styles.metricValue}>
                        {outboundBadge.label}
                        <br />
                        <span style={{ fontSize: '0.85em', color: tokens.colorNeutralForeground4 } as CSSProperties}>
                          J: {branch.outbound_avg_jitter_ms.toFixed(1)}ms | L:{' '}
                          {branch.outbound_avg_latency_ms.toFixed(1)}ms | PL:{' '}
                          {branch.outbound_avg_packet_loss_percent.toFixed(2)}%
                        </span>
                      </Text>
                    </div>
                  </td>
                  <td className={styles.rowCell}>
                    <Text className={styles.metricValue}>{branch.call_count}</Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
